import React, { useState } from 'react';
import ResponderAlertModal from 'features/Responder/components/ResponderAlertModal';
import { Button, OverlayTrigger, Stack, Tooltip } from 'react-bootstrap';
import { flushSync } from 'react-dom';
import { FaPencilAlt, FaPlus, FaTrash } from 'react-icons/fa';
import { useTours } from 'features/Responder/features/Tours/contexts/TourContext';
import './tourList.css';

const renderResponderTooltip = (id, text) => (
  <Tooltip id={id} className="responder-tooltip">
    {text}
  </Tooltip>
);

/**
 * Simple tour picker list for responders.
 * Highlights the active tour and notifies TourContext on selection.
 * @returns {JSX.Element}
 */
export default function TourList() {
  const {
    tours,
    selectTour,
    activeTourData,
    deleteTour,
    setBuilderTourData,
    builderHydrationRef,
    setTourEditing,
  } = useTours();
  const [pendingDelete, setPendingDelete] = useState(null);
  const handleEditTour = (tour) => {
    if (!tour) return;
    if (builderHydrationRef) {
      builderHydrationRef.current = tour.id ?? 'new';
    }
    flushSync(() => {
      setTourEditing(true);
    });
    setBuilderTourData(tour);
    selectTour(tour);
  };

  const handleCreateTour = () => {
    if (builderHydrationRef) {
      builderHydrationRef.current = null;
    }
    flushSync(() => {
      setTourEditing(true);
    });
    setBuilderTourData(null);
    selectTour(null);
  };

  return (
    <div className="w-100 d-flex flex-column gap-2 p-2 h-100" style={{ minHeight: 0 }}>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="mb-0">Manage Tours</h3>
        <OverlayTrigger
          placement="top"
          overlay={renderResponderTooltip('tour-list-create-top', 'Create tour')}
        >
          <Button
            variant="outline-light"
            size="sm"
            className="tour-list-create-btn responder-interactive-action d-flex align-items-center gap-2"
            onClick={handleCreateTour}
          >
            <FaPlus />
            <span>Create</span>
          </Button>
        </OverlayTrigger>
      </div>
      <Stack gap={2} className="flex-grow-1 overflow-auto" style={{ minHeight: 0 }}>
        {Array.isArray(tours) && tours.length ? (
          tours.map((tour) => {
            const isActive = activeTourData && activeTourData.id === tour.id;
            return (
              <div
                key={tour.id || tour.name}
                className={`tour-list-item responder-interactive-item ${
                  isActive ? 'is-selected' : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => selectTour(tour)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    selectTour(tour);
                  }
                }}
              >
                <div className="d-flex justify-content-between align-items-start py-2 px-3">
                  <div>
                    <div className="fw-semibold">{tour.name || 'Untitled Tour'}</div>
                    {tour.nodes?.length ? (
                      <div className="small text-muted">
                        {tour.nodes.length} checkpoint{tour.nodes.length === 1 ? '' : 's'}
                      </div>
                    ) : null}
                  </div>
                  <div className="d-flex flex-column align-items-center gap-2">
                    <OverlayTrigger
                      placement="top"
                      overlay={renderResponderTooltip(`tour-list-edit-${tour?.id}`, 'Edit tour')}
                    >
                      <button
                        type="button"
                        className="tour-list-icon-btn responder-interactive-action text-primary"
                        aria-label={`Edit ${tour.name || 'tour'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTour(tour);
                        }}
                      >
                        <FaPencilAlt />
                      </button>
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={renderResponderTooltip(
                        `tour-list-delete-${tour?.id}`,
                        'Delete tour',
                      )}
                    >
                      <button
                        type="button"
                        className="tour-list-icon-btn responder-interactive-action text-danger"
                        aria-label={`Delete ${tour.name || 'tour'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(tour);
                        }}
                      >
                        <FaTrash />
                      </button>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-muted small">No tours saved yet.</div>
        )}
      </Stack>
      <OverlayTrigger
        placement="top"
        overlay={renderResponderTooltip('tour-list-create-bottom', 'Create tour')}
      >
        <Button
          variant="outline-light"
          className="tour-list-create-btn responder-interactive-action mt-2 d-flex align-items-center justify-content-center gap-2"
          onClick={handleCreateTour}
        >
          <FaPlus />
          <span>Create New Tour</span>
        </Button>
      </OverlayTrigger>
      <ResponderAlertModal
        show={!!pendingDelete}
        title="Delete tour"
        message={`Are you sure you want to permanently delete "${
          pendingDelete?.name || 'this tour'
        }"?`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onCancel={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (pendingDelete?.id == null) {
            setPendingDelete(null);
            return;
          }
          try {
            await deleteTour(pendingDelete.id);
          } catch (error) {
            console.error('Failed to delete tour:', error);
          }
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
